import { t } from 'i18next';
import type { IObservableArray } from 'mobx';

import type { ERecommendationType, Goal, Recommendation, TTDCampaign } from '@feathr/blackbox';
import { toast } from '@feathr/components';

import { updateRecommendationStatus } from './CampaignRecommendations/RecommendationModal';

interface IRecommendationSaveAllProps {
  /** The Tradedesk campaign */
  campaign: TTDCampaign;
  /** The recommendation models */
  recommendations: IObservableArray<Recommendation>;
  /** Refreshes the recommendations collection to see if any recommendation statuses have changed */
  onRefreshCollection: () => Promise<void>;
  /** The user's timezone from user settings */
  timezone: string;
  /** The goals for the campaign */
  goals: IObservableArray<Goal>;
}

/**
 * Reconciles all recommendations for a campaign, checking the current campaign values against the recommended values and updates the status of the recommendation if necessary
 *
 * This will be done by the backend in the future
 * @param campaign
 * @param recommendations
 * @param onRefreshCollection
 * @param timezone
 * @returns a function that reconciles all recommendations for a campaign
 */
export async function reconcileAllRecommendations({
  campaign,
  recommendations,
  onRefreshCollection,
  timezone,
  goals,
}: IRecommendationSaveAllProps): Promise<void> {
  for (const recommendation of recommendations) {
    const type = recommendation.get('type') as ERecommendationType;

    const saveFunction = updateRecommendationStatus({
      type,
      campaign,
      recommendation,
      onRefreshCollection,
      timezone,
      context: 'report',
      goals,
    });

    try {
      await saveFunction();
    } catch (e) {
      toast(
        t('An error occurred while checking for outdated recommendations: {{title}}', {
          title: recommendation.get('title'),
          interpolation: { escapeValue: false },
        }),
        {
          type: 'error',
        },
      );
    }
  }
}
